import { useCallback } from 'react';
import {
  isBiometricEnabled,
  promptBiometricAuth,
  setBiometricEnabled,
} from '@/domains/shared/components/biometrics/Biometrics';
import { useIdentityBoot } from '@/domains/shared/identity/IdentityBootContext';

/**
 * Thin selector + imperative wrapper around the identity boot state.
 *
 * Reading `BIOMETRIC_ENABLED` and the OS biometric availability now
 * happens once, inside `IdentityBootProvider`, after migration has
 * fully completed. This hook just surfaces those values and provides
 * the imperative `enable` / `disable` / `authenticate` helpers used
 * by settings screens and `PinManager`.
 */
export const useToggleBiometrics = () => {
  const boot = useIdentityBoot();

  const enable = useCallback(async () => {
    if (!boot.biometricAvailable) return false;
    await setBiometricEnabled(true);
    const enabled = await isBiometricEnabled();
    boot.setBiometricEnabled(enabled);
    return true;
  }, [boot]);

  const disable = useCallback(async () => {
    await setBiometricEnabled(false);
    const enabled = await isBiometricEnabled();
    boot.setBiometricEnabled(enabled);
  }, [boot]);

  const authenticate = useCallback(async () => {
    return await promptBiometricAuth();
  }, []);

  return {
    available: boot.biometricAvailable,
    enabled: boot.biometricEnabled,
    initialised: boot.ready,
    enable,
    disable,
    authenticate,
  };
};
